import {
  removeCleanOrNotDefined,
  useCreateLoginPage,
  useCreateSignUpPage,
  useLoginFormData,
  useSignUpFormData,
  useUpdateLoginPage,
  useUpdateSignUpPage,
  wrapHandleSubmitWithAction,
} from "@/api/cms";
import { LoginSignupForm } from "@/components/cms/LoginSignupForm";
import { Switch } from "@/components/form-items/AbstractForm";
import { DoubleMultilingualField } from "@/components/form-items/MultilingualField";
import { useToast } from "@/hooks/use-toast";
import {
  constructLocaleDict,
  multilingualFieldSchema,
  SupportedLocale,
} from "@/i18n";
import CMSLayout from "@/layout/CMSLayout.tsx";
import {
  baseCmsFormSchema,
  loginContentSchema,
  signUpContentSchema,
} from "@/lib/cms/cms-types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { FormProvider, Path, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const formSchema = z.object({
  login: multilingualFieldSchema(
    baseCmsFormSchema.merge(loginContentSchema.omit({ id: true })),
  ),
  signup: multilingualFieldSchema(
    baseCmsFormSchema
      .omit({ published: true })
      .merge(signUpContentSchema.omit({ id: true })),
  ),
  //   published: multilingualFieldSchema(baseCmsFormSchema.shape.published),
});

type FormData = z.infer<typeof formSchema>;

const AuthCMS = () => {
  const { t } = useTranslation();

  const { toast } = useToast();

  const { data: loginFormData } = useLoginFormData();
  const createLogin = useCreateLoginPage();
  const updateLogin = useUpdateLoginPage();

  const { data: signupFormData } = useSignUpFormData();
  const createSignup = useCreateSignUpPage();
  const updateSignup = useUpdateSignUpPage();

  const defaultLoginValues: FormData["login"] = useMemo(() => {
    return constructLocaleDict((lang) => {
      const login = loginFormData?.[lang];
      return {
        published: login?.published ?? false,
        pageName: login?.pageName ?? "",
        subtitle: login?.content?.subtitle,
      };
    });
  }, [loginFormData]);

  const defaultSignupValues: FormData["signup"] = useMemo(() => {
    return constructLocaleDict((lang) => {
      const signup = signupFormData?.[lang];
      return {
        pageName: signup?.pageName ?? "",
        subtitle: signup?.content?.subtitle,
      };
    });
  }, [signupFormData]);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      login: defaultLoginValues,
      signup: defaultSignupValues,
    },
  });

  useEffect(() => {
    form.reset({
      ...form.getValues(),
      login: defaultLoginValues,
    });
  }, [defaultLoginValues, form]);

  useEffect(() => {
    form.reset({
      ...form.getValues(),
      signup: defaultSignupValues,
    });
  }, [defaultSignupValues, form]);

  const onSubmit = (submittedData: FormData) => {
    Object.keys(submittedData.login).forEach((key) => {
      const localeData = submittedData.login[key as SupportedLocale];
      const baseData = loginFormData?.[key as SupportedLocale];
      if (
        !localeData ||
        !form.getFieldState(`login.${key}` as Path<FormData>).isDirty
      )
        return;

      const mutation = baseData?.content.id ? updateLogin : createLogin;

      const { pageName, published, ...content } = localeData;

      mutation.mutate({
        id: baseData?.content.id ?? "",
        lang: key as SupportedLocale,
        data: {
          pageName,
          published,
          content,
        },
      });
    });
    Object.keys(submittedData.signup).forEach((key) => {
      const localeData = submittedData.signup[key as SupportedLocale];
      const baseData = signupFormData?.[key as SupportedLocale];
      if (
        !localeData ||
        (!form.getFieldState(`signup.${key}` as Path<FormData>).isDirty &&
          !form.getFieldState(`login.${key}.published` as Path<FormData>)
            .isDirty)
      )
        return;

      const mutation = baseData?.content.id ? updateSignup : createSignup;

      const { pageName, ...content } = localeData;

      mutation.mutate(
        {
          id: baseData?.content.id ?? "",
          lang: key as SupportedLocale,
          data: {
            pageName,
            published:
              submittedData.login?.[key as SupportedLocale]?.published ?? false,
            content,
          },
        },
        {
          onSuccess: () => {
            toast({
              title: t("success"),
              description: t("AuthentificationUpdatedSucess"),
              duration: 1000,
              variant: "success",
            });
          },
          onError: (error) => {
            console.error(error);
            toast({
              title: t("error"),
              description: t("AuthentificationUpdatedError"),
              duration: 1000,
              variant: "error",
            });
          },
        },
      );
    });
  };
  const cleanBeforeSubmit = () => {
    removeCleanOrNotDefined(form, loginFormData, "login");
    removeCleanOrNotDefined(form, signupFormData, "signup");
  };

  return (
    <CMSLayout
      pageName={t("cmsTabAuthentification")}
      havePublishedSwitch={false}
      formIds={["login-signup"]}
    >
      <FormProvider {...form}>
        <form
          onSubmit={wrapHandleSubmitWithAction(
            form,
            cleanBeforeSubmit,
            onSubmit,
          )}
          id="login-signup"
          className="flex w-full gap-4"
        >
          <DoubleMultilingualField
            className="w-full"
            corner={(lang) => (
              <Switch dispLabel={false} name={`login.${lang}.published`} />
            )}
            render={(lang) => <LoginSignupForm language={lang} />}
          />
        </form>
      </FormProvider>
    </CMSLayout>
  );
};

export default AuthCMS;
